import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Gated, useHasPermission } from '@/auth/permissions';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { AssignedHrSelect } from '@/components/AssignedHrSelect';
import { IQ_ASSESSMENT_LABEL } from '@/lib/stageLabels';
import { StatusPill } from '@/components/StatusPill';
import { PipelineTimeline } from '@/components/PipelineTimeline';
import { CandidatePhotoEditor } from '@/components/CandidatePhotoEditor';
import { CombinedAssessmentInviteButton } from '@/components/CombinedAssessmentInviteButton';
import { PanelInterviewsTab } from '@/components/PanelInterviewsTab';
import { ClientReportTab } from '@/components/ClientReportTab';
import { OfferTab } from '@/components/OfferTab';
import { StageActionBar } from '@/components/candidate/StageActionBar';
import {
  EmptyStateCard,
  VideoBotResponseCard,
  McqResultView,
} from '@/components/candidate/AssessmentResultViews';
import { CandidateResumeTabContent, CompanyResumeViewModal } from '@/components/candidate/CandidateResumeTab';
import { useCandidateDetail, STAGE_LABEL } from '@/hooks/useCandidateDetail';
import { fmtDateTimeIST } from '@/lib/format';
import {
  resolveCandidateDetailTab,
  resolvePanelTabFromDeeplink,
  nextTabSearchParams,
} from '@/lib/candidateDetailTab';
import {
  EvaluationsPillRow,
  NewEvaluationModal,
  CloseEvaluationDialog,
  ClosedEvalBanner,
} from '@/components/evaluation';
import { ArrowLeft, ChevronDown, Pencil, Trash2, AlertTriangle } from 'lucide-react';

export default function CandidateDetail() {
  const {
    id,
    navigate,
    c,
    evaluations,
    evaluationsLoading,
    effectiveEvaluationId,
    setSelectedEvaluationId,
    setActiveRecordId,
    isReadOnlyForActions,
    view,
    stageStatuses,
    pipelineStageSet,
    panelRounds,
    orderedStageTabs,
    companyResume,
    resumeTemplate,
    v1Links,
    assessmentReadiness,
    activityScope,
    setActivityScope,
    activityItems,
    activityLoading,
    activityError,
    invalidateCandidateViews,
    refetchEvaluations,
    refetchLinks,
    refetchCombinedInviteStatus,
    genLinkOpen,
    setGenLinkOpen,
    genLinkStage,
    setGenLinkStage,
    genLinkExpiry,
    setGenLinkExpiry,
    combinedWarnOpen,
    setCombinedWarnOpen,
    setCombinedWarnAction,
    statusPopoverOpen,
    setStatusPopoverOpen,
    statusDraft,
    setStatusDraft,
    statusReason,
    setStatusReason,
    statusSaving,
    deleteOpen,
    setDeleteOpen,
    deleting,
    newEvalOpen,
    setNewEvalOpen,
    closeEvalOpen,
    setCloseEvalOpen,
    resumeViewOpen,
    setResumeViewOpen,
    generating,
    downloadingResume,
    uploadingResume,
    fileInputRef,
    canReplaceResume,
    resumeFile,
    hrEditOpen,
    setHrEditOpen,
    hrSaving,
    saveAssignedHr,
    onPickResumeFile,
    onResumeFileSelected,
    downloadOriginalResume,
    generateResume,
    generateLink,
    requestSendStageLink,
    revokeLink,
    requestReissueLink,
    confirmCombinedWarn,
    copyLink,
    openStatusPopover,
    submitStatusChange,
    submitDelete,
  } = useCandidateDetail();

  const canViewEvaluation = useHasPermission('evaluation.view');
  const canCreateEvaluation = useHasPermission('evaluation.create');
  const canEditEvaluation = useHasPermission('evaluation.edit');
  const canCloseEvaluation = useHasPermission('evaluation.close');
  const canViewClientReport = useHasPermission('report.view_internal');
  const canEditCandidate = useHasPermission('candidate.edit');

  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = React.useState('resume');
  const [activePlayingResponseId, setActivePlayingResponseId] = React.useState(null);

  const availableTabValues = React.useMemo(() => {
    const vals = new Set((orderedStageTabs || []).map((t) => t.value));
    if (canViewClientReport) vals.add('client-report');
    vals.add('activity');
    vals.add('resume');
    return vals;
  }, [orderedStageTabs, canViewClientReport]);

  // Stable key so orderedStageTabs identity churn does not re-apply deeplink tab.
  const availableTabKey = React.useMemo(
    () => [...availableTabValues].sort().join('|'),
    [availableTabValues],
  );
  const tabParam = searchParams.get('tab');
  const panelParam = searchParams.get('panel');
  const tabsReady = (orderedStageTabs || []).length > 0;

  // Assessment-completion / panel deeplinks pass ?tab=; sync only when the
  // tab query (or resolved panel tab) changes — never fight a user click.
  React.useEffect(() => {
    const resolvedFromPanel = resolvePanelTabFromDeeplink({
      tabFromUrl: tabParam,
      panelId: panelParam,
      panels: view?.panels || [],
    });
    const effectiveTabParam = resolvedFromPanel || tabParam;
    if (!effectiveTabParam) return;
    const resolved = resolveCandidateDetailTab({
      tabFromUrl: effectiveTabParam,
      availableTabValues,
      tabsReady,
    });
    if (resolved != null) setActiveTab(resolved);
    // When legacy tab=panels resolves to panel-{stage}, write the concrete tab once.
    if (
      resolved
      && resolved !== tabParam
      && resolved.startsWith('panel-')
      && (tabParam === 'panels' || !tabParam)
    ) {
      const next = nextTabSearchParams(searchParams, resolved);
      setSearchParams(next, { replace: true });
    }
    // availableTabKey stands in for Set identity; availableTabValues used inside.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: tabParam/panelParam/availableTabKey/tabsReady
  }, [tabParam, panelParam, availableTabKey, tabsReady, view?.panels]);

  if (evaluationsLoading) {
    return <div className="p-8 text-sm text-muted-foreground">Loading evaluations…</div>;
  }

  if (!effectiveEvaluationId) {
    return (
      <div className="space-y-4 p-8" data-testid="candidate-no-evaluation-prompt">
        <Button variant="ghost" size="sm" className="gap-1.5 -ml-2" onClick={() => navigate('/portal/candidates')}>
          <ArrowLeft className="h-4 w-4" /> All candidates
        </Button>
        <Card className="card-elevated">
          <CardContent className="p-6 text-sm text-muted-foreground">
            No evaluation is available for this candidate. Create an evaluation to load stage data.
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!c) return <div className="p-8 text-sm text-muted-foreground">Loading candidate…</div>;

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" className="gap-1.5 -ml-2" onClick={() => navigate('/portal/candidates')}><ArrowLeft className="h-4 w-4" /> All candidates</Button>

      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div className="flex items-start gap-4">
          <CandidatePhotoEditor
            candidateId={id}
            candidateName={c.full_name}
            photoUrl={c.photo_url}
            size={56}
            evaluationId={effectiveEvaluationId}
          />
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-semibold tracking-tight" data-testid="candidate-header-name">{c.full_name}</h1>
              {canEditCandidate ? (
                <Popover open={statusPopoverOpen} onOpenChange={(v) => { if (v) openStatusPopover(); else setStatusPopoverOpen(false); }}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="group inline-flex items-center gap-1 rounded-full outline-none transition-opacity hover:opacity-80 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      data-testid="candidate-status-pill-trigger"
                      aria-label="Change candidate status"
                    >
                      <StatusPill status={c.status} />
                      <ChevronDown className="h-3.5 w-3.5 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-[360px] p-4" data-testid="candidate-status-popover">
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-semibold">Change lifecycle status</p>
                        <p className="text-xs text-muted-foreground">
                          Distinct from pipeline stage. Hold or Reject disables Quick Actions until reopened.
                        </p>
                      </div>
                      <RadioGroup
                        value={statusDraft}
                        onValueChange={setStatusDraft}
                        className="grid gap-2"
                        data-testid="candidate-status-options"
                      >
                        {[
                          { value: 'in_progress', label: 'In Progress', hint: 'Active in pipeline' },
                          { value: 'on_hold', label: 'On Hold', hint: 'Pause activities; client/internal review' },
                          { value: 'rejected', label: 'Rejected', hint: 'Closed — not moving forward' },
                        ].map((opt) => (
                          <Label
                            key={opt.value}
                            htmlFor={`status-opt-${opt.value}`}
                            className={`flex cursor-pointer items-start gap-3 rounded-lg border p-2.5 text-sm transition-colors ${statusDraft === opt.value ? 'border-primary bg-primary/5' : 'hover:bg-accent'}`}
                          >
                            <RadioGroupItem id={`status-opt-${opt.value}`} value={opt.value} className="mt-0.5" data-testid={`status-option-${opt.value}`} />
                            <div className="space-y-0.5">
                              <p className="font-medium leading-none">{opt.label}</p>
                              <p className="text-xs text-muted-foreground">{opt.hint}</p>
                            </div>
                          </Label>
                        ))}
                      </RadioGroup>
                      <div>
                        <Label htmlFor="status-reason" className="text-xs font-medium text-muted-foreground">
                          Reason <span className="text-rose-600">*</span>
                        </Label>
                        <Textarea
                          id="status-reason"
                          value={statusReason}
                          onChange={(e) => setStatusReason(e.target.value)}
                          placeholder="Why is this status changing? (visible in audit log)"
                          rows={3}
                          className="mt-1 resize-none text-sm"
                          data-testid="candidate-status-reason"
                        />
                      </div>
                      <div className="flex justify-end gap-2 pt-1">
                        <Button variant="ghost" size="sm" onClick={() => setStatusPopoverOpen(false)} disabled={statusSaving}>Cancel</Button>
                        <Button
                          size="sm"
                          onClick={submitStatusChange}
                          disabled={statusSaving || statusReason.trim().length < 3}
                          data-testid="candidate-status-save"
                        >
                          {statusSaving ? 'Saving…' : 'Save change'}
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              ) : (
                <StatusPill status={c.status} />
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              {c.role_label} · {c.department_label}{c.sub_department_label ? ` · ${c.sub_department_label}` : ''} ·{' '}
              {canEditCandidate ? (
                <Popover open={hrEditOpen} onOpenChange={setHrEditOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 rounded-md border border-dashed border-muted-foreground/30 px-1.5 py-0.5 text-sm font-medium text-foreground hover:border-primary hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      data-testid="assigned-hr-trigger"
                      aria-label="Change assigned HR"
                    >
                      <span className="text-xs text-muted-foreground">Assigned to</span>{' '}
                      <span>{c.assigned_hr && c.assigned_hr !== '—' ? c.assigned_hr : 'Unassigned'}</span>
                      <Pencil className="h-3 w-3 text-muted-foreground" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-[320px] p-3" data-testid="assigned-hr-popover">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Assigned HR owner
                    </p>
                    <AssignedHrSelect
                      value={c.assigned_hr_id || ''}
                      onChange={saveAssignedHr}
                      fallbackLabel={c.assigned_hr || ''}
                      disabled={hrSaving}
                      placeholder={hrSaving ? 'Saving…' : 'Select HR owner'}
                      testId="assigned-hr-select"
                    />
                    <p className="mt-2 text-[11px] text-muted-foreground">
                      Active admin, HR Manager, or HR Recruiter users only. The change is logged in the audit trail.
                    </p>
                  </PopoverContent>
                </Popover>
              ) : (
                <span className="inline-flex items-center gap-1 text-sm font-medium text-foreground" data-testid="assigned-hr-readonly">
                  <span className="text-xs text-muted-foreground">Assigned to</span>{' '}
                  <span>{c.assigned_hr && c.assigned_hr !== '—' ? c.assigned_hr : 'Unassigned'}</span>
                </span>
              )}
            </p>
            <p className="text-xs text-muted-foreground">{c.email} · {c.contact_number}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="candidate-quick-actions">
          <CombinedAssessmentInviteButton
            candidateId={id}
            evaluationId={effectiveEvaluationId}
            pipelineStageSet={pipelineStageSet}
            disabled={isReadOnlyForActions || c.status !== 'in_progress'}
            assessmentReadiness={assessmentReadiness}
            onSent={() => {
              refetchLinks();
              void refetchCombinedInviteStatus();
              void invalidateCandidateViews();
            }}
          />
          <Gated need="candidate.bulk_enrichment">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => navigate(
                `/portal/resume-enrichment/${id}?evaluation_id=${encodeURIComponent(effectiveEvaluationId)}`,
              )}
              data-testid="open-enrichment-workspace"
            >
              <Pencil className="h-3.5 w-3.5" /> Edit in Enrichment workspace
            </Button>
          </Gated>
          <Gated need="candidate.delete">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
              onClick={() => setDeleteOpen(true)}
              data-testid="delete-candidate-button"
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete candidate
            </Button>
          </Gated>
        </div>
      </div>

      <AlertDialog open={deleteOpen} onOpenChange={(v) => { if (!deleting) setDeleteOpen(v); }}>
        <AlertDialogContent data-testid="delete-candidate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this candidate?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium text-foreground">{c.full_name}</span> and all of
              their evaluations will be removed from the Candidates list. This action is
              recorded in the audit trail. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="delete-candidate-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); submitDelete(); }}
              disabled={deleting}
              className="bg-rose-600 text-white hover:bg-rose-700 focus:ring-rose-600"
              data-testid="delete-candidate-confirm"
            >
              {deleting ? 'Deleting…' : 'Delete candidate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={combinedWarnOpen}
        onOpenChange={(v) => {
          setCombinedWarnOpen(v);
          if (!v) setCombinedWarnAction(null);
        }}
      >
        <AlertDialogContent data-testid="combined-invite-warn-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Combined assessment already sent</AlertDialogTitle>
            <AlertDialogDescription>
              A combined assessment invite was already sent for this evaluation.
              Sending a separate stage link will create an additional invitation.
              Do you want to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="combined-invite-warn-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmCombinedWarn(); }}
              data-testid="combined-invite-warn-confirm"
            >
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Sprint 14.1b — Evaluations pill row + closed banner. Promotes the
          previously disabled "Interview records" scaffold to a real entity.
          Mirrors selection into ``activeRecordId`` so the legacy view-merging
          logic below continues to work until Phase 2 ships per-evaluation
          data scoping. Hidden without evaluation.view (fail closed). */}
      {canViewEvaluation && (
        <EvaluationsPillRow
          evaluations={evaluations}
          selectedEvaluationId={effectiveEvaluationId}
          onSelect={(eid) => {
            setSelectedEvaluationId(eid);
            setActiveRecordId(eid);
            // Keep eval= in sync so refresh / Strict Mode match the pill.
            if (searchParams.get('eval') !== eid) {
              const next = new URLSearchParams(searchParams);
              next.set('eval', eid);
              next.delete('evaluation_id');
              setSearchParams(next, { replace: true });
            }
          }}
          onNewEvaluation={() => setNewEvalOpen(true)}
          loading={evaluationsLoading}
          canCreate={canCreateEvaluation}
        />
      )}

      {(() => {
        const sel = evaluations.find((e) => e.id === effectiveEvaluationId);
        return sel?.status === 'closed' ? (
          <ClosedEvalBanner evaluation={sel} />
        ) : null;
      })()}

      {/* Sprint 15.2 — Read-only signal derived from the selected evaluation.
          Cascaded onto stage tabs below via <fieldset disabled> so every
          mutate-style button/input becomes inert. AI Report + Activity tabs
          remain fully enabled per Sprint 14 D4 charter. */}

      {/* Sprint 14.1b — Close evaluation CTA. Shown only when the selected evaluation is Active. */}
      {(() => {
        const sel = evaluations.find((e) => e.id === effectiveEvaluationId);
        return sel && sel.status === 'active' && canCloseEvaluation ? (
          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-muted-foreground hover:text-rose-600"
              onClick={() => setCloseEvalOpen(true)}
              data-testid="close-evaluation-button"
            >
              Close evaluation
            </Button>
          </div>
        ) : null;
      })()}

      {stageStatuses.length > 0 && (
        <Card className="card-elevated">
          <CardContent className="p-4">
            <PipelineTimeline stages={stageStatuses} />
          </CardContent>
        </Card>
      )}

      <Tabs
        value={activeTab}
        className="space-y-4"
        onValueChange={(value) => {
          setActiveTab(value);
          if (value !== 'videobot') setActivePlayingResponseId(null);
          // Keep URL tab as SSOT so deeplink sync cannot overwrite the click.
          if (searchParams.get('tab') !== value) {
            setSearchParams(nextTabSearchParams(searchParams, value), { replace: true });
          }
        }}
      >
        {/* Mobile: single-row horizontal scroll. Wrapping + base TabsList h-9
            caused wrapped triggers to overflow onto tab content. Desktop/tablet
            still render as one row when width allows; overflow-x only appears
            when tabs exceed the viewport. */}
        <TabsList
          className="flex h-auto min-h-9 w-full max-w-full flex-nowrap justify-start gap-1 overflow-x-auto bg-secondary/60 [scrollbar-width:thin] [&>button]:shrink-0"
          data-testid="candidate-detail-tabs"
        >
          {/* Pipeline stage tabs follow pinned version order (same as PipelineTimeline).
              Client Report + Activity stay last — they are not pipeline stages. */}
          {(orderedStageTabs || []).map((tab) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              data-testid={tab.testId}
            >
              {tab.label}
            </TabsTrigger>
          ))}
          {canViewClientReport && (
            <TabsTrigger value="client-report" data-testid="tab-client-report">Client Report</TabsTrigger>
          )}
          <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="resume">
          <CandidateResumeTabContent
            isReadOnlyForActions={isReadOnlyForActions}
            fileInputRef={fileInputRef}
            onResumeFileSelected={onResumeFileSelected}
            onPickResumeFile={onPickResumeFile}
            companyResume={companyResume}
            resumeFile={resumeFile}
            generating={generating}
            generateResume={generateResume}
            setResumeViewOpen={setResumeViewOpen}
            downloadingResume={downloadingResume}
            downloadOriginalResume={downloadOriginalResume}
            uploadingResume={uploadingResume}
            canReplaceResume={canReplaceResume}
          />
        </TabsContent>

        {pipelineStageSet.has('videobot') && (
        <TabsContent value="videobot" className="space-y-4">
          <fieldset disabled={isReadOnlyForActions} className="contents" data-testid="videobot-tab-fieldset">
          <StageActionBar
            stageCode="VIDEOBOT"
            stageLabel="Video Bot"
            locked={c.status !== 'in_progress'}
            lockedReason={c.status === 'on_hold' ? 'on hold' : 'rejected'}
            configWarning={assessmentReadiness.getStageWarning('VIDEOBOT')}
            configBlocked={!assessmentReadiness.isStageReady('VIDEOBOT')}
            canMutate={canEditEvaluation}
            onSend={() => requestSendStageLink('VIDEOBOT')}
            links={(v1Links || []).filter((l) => l.stage_code === 'VIDEOBOT')}
            onCopy={copyLink}
            onRevoke={revokeLink}
            onReissue={requestReissueLink}
            testIdPrefix="videobot"
          />
          {!view.videobot ? (
            <EmptyStateCard title="No Video Bot submission yet" body="Send a Video Bot invitation link above to begin." />
          ) : (
            <div className="space-y-4">
              <Card className="card-elevated"><CardContent className="p-5 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold" data-testid="videobot-submission-header">
                    {view.videobot.status === 'submitted' ? 'Submitted' : 'In progress'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {view.videobot.submitted_at
                      ? `on ${fmtDateTimeIST(view.videobot.submitted_at)} · `
                      : `started ${view.videobot.started_at ? fmtDateTimeIST(view.videobot.started_at) : ''} · `}
                    {(view.videobot.responses || []).length} responses
                  </p>
                </div>
                <StatusPill status={view.videobot.status} />
              </CardContent></Card>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {(view.videobot.responses || []).map((r, i) => {
                  const responseKey = r.id || r.question_id || String(i);
                  return (
                    <VideoBotResponseCard
                      key={responseKey}
                      r={r}
                      index={i + 1}
                      candidateId={id}
                      onChanged={() => invalidateCandidateViews()}
                      isPlaying={activePlayingResponseId === responseKey}
                      onPlay={() => setActivePlayingResponseId(responseKey)}
                      onStop={() => setActivePlayingResponseId((curr) => (curr === responseKey ? null : curr))}
                    />
                  );
                })}
              </div>
            </div>
          )}
          </fieldset>
        </TabsContent>
        )}

        {pipelineStageSet.has('mcq') && (
        <TabsContent value="mcq" className="space-y-4">
          <fieldset disabled={isReadOnlyForActions} className="contents" data-testid="mcq-tab-fieldset">
          <StageActionBar
            stageCode="MCQ"
            stageLabel={IQ_ASSESSMENT_LABEL}
            locked={c.status !== 'in_progress'}
            lockedReason={c.status === 'on_hold' ? 'on hold' : 'rejected'}
            configWarning={assessmentReadiness.getStageWarning('MCQ')}
            configBlocked={!assessmentReadiness.isStageReady('MCQ')}
            canMutate={canEditEvaluation}
            onSend={() => requestSendStageLink('MCQ')}
            links={(v1Links || []).filter((l) => l.stage_code === 'MCQ')}
            onCopy={copyLink}
            onRevoke={revokeLink}
            onReissue={requestReissueLink}
            testIdPrefix="mcq"
          />
          {!view.mcq ? <EmptyStateCard title="No IQ Assessment submission yet" body="Send an IQ Assessment invitation link above to begin." /> : <McqResultView result={view.mcq.result} evalBlock={view.mcq.eval} recording={view.mcq.recording} integrityReview={view.mcq.integrity_review} submittedAt={view.mcq.submitted_at} assessmentId={view.mcq.id} candidateId={id} onChanged={() => invalidateCandidateViews()} />}
          </fieldset>
        </TabsContent>
        )}

        {pipelineStageSet.has('tech_assessment') && (
        <TabsContent value="tech-assessment" className="space-y-4">
          <fieldset disabled={isReadOnlyForActions} className="contents" data-testid="tech-assessment-tab-fieldset">
          <StageActionBar
            stageCode="TECH_ASSESSMENT"
            stageLabel="Tech Assessment"
            locked={c.status !== 'in_progress'}
            lockedReason={c.status === 'on_hold' ? 'on hold' : 'rejected'}
            configWarning={assessmentReadiness.getStageWarning('TECH_ASSESSMENT')}
            configBlocked={!assessmentReadiness.isStageReady('TECH_ASSESSMENT')}
            canMutate={canEditEvaluation}
            onSend={() => requestSendStageLink('TECH_ASSESSMENT')}
            links={(v1Links || []).filter((l) => l.stage_code === 'TECH_ASSESSMENT')}
            onCopy={copyLink}
            onRevoke={revokeLink}
            onReissue={requestReissueLink}
            testIdPrefix="tech-assessment"
          />
          {!view.tech_assessment ? (
            <EmptyStateCard title="No Tech Assessment submission yet" body="Send a Tech Assessment invitation link above to begin." />
          ) : (
            <McqResultView
              result={view.tech_assessment.result}
              evalBlock={view.tech_assessment.eval}
              recording={view.tech_assessment.recording}
              integrityReview={view.tech_assessment.integrity_review}
              submittedAt={view.tech_assessment.submitted_at}
              assessmentId={view.tech_assessment.id}
              candidateId={id}
              onChanged={() => invalidateCandidateViews()}
              reevalPath={`/candidates/${id}/tech-assessment/assessment/${view.tech_assessment.id}/reeval`}
              stageCode="TECH_ASSESSMENT"
              testIdPrefix="tech-assessment"
            />
          )}
          </fieldset>
        </TabsContent>
        )}

        {panelRounds.map((round, i) => (
        <TabsContent key={round.id || `panel-content-${i}`} value={`panel-${round.id || i}`} className="space-y-4">
          <fieldset disabled={isReadOnlyForActions} className="contents" data-testid={`panel-tab-fieldset-${i}`}>
          {c.status !== 'in_progress' && (
            <Card className="border-amber-200 bg-amber-50/40"><CardContent className="p-3 text-xs text-amber-800 flex items-center gap-2">
              <AlertTriangle className="h-3.5 w-3.5" />
              Candidate is {c.status === 'on_hold' ? 'on hold' : 'rejected'} — scheduling new panel interviews is disabled until the status is set back to In Progress.
            </CardContent></Card>
          )}
          <PanelInterviewsTab
            candidateId={id}
            candidateEmail={c.email || ''}
            disabled={c.status !== 'in_progress'}
            panelKind="tech"
            evaluationId={effectiveEvaluationId}
            panelRounds={[round]}
          />
          </fieldset>
        </TabsContent>
        ))}

        {pipelineStageSet.has('offer') && (
        <TabsContent value="offer" className="space-y-4">
          <fieldset disabled={isReadOnlyForActions} className="contents" data-testid="offer-tab-fieldset">
          {c.status !== 'in_progress' && (
            <Card className="border-amber-200 bg-amber-50/40"><CardContent className="p-3 text-xs text-amber-800 flex items-center gap-2">
              <AlertTriangle className="h-3.5 w-3.5" />
              Candidate is {c.status === 'on_hold' ? 'on hold' : 'rejected'} — Offer actions are disabled until the status is set back to In Progress.
            </CardContent></Card>
          )}
          <OfferTab
            candidateId={id}
            candidateEmail={c.email || ''}
            evaluationId={effectiveEvaluationId}
            disabled={c.status !== 'in_progress'}
          />
          </fieldset>
        </TabsContent>
        )}

        {canViewClientReport && (
          <TabsContent value="client-report">
            <ClientReportTab candidateId={id} evaluationId={effectiveEvaluationId} />
          </TabsContent>
        )}

        <TabsContent value="activity">
          <Card className="card-elevated"><CardContent className="p-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold">Activity log</p>
                <p className="text-xs text-muted-foreground">
                  Timeline of meaningful events — status changes, links sent, files uploaded, assessments, and reports.
                </p>
              </div>
              <div className="flex items-center gap-1 rounded-md border p-0.5" data-testid="activity-scope-toggle">
                <Button
                  type="button"
                  size="sm"
                  variant={activityScope === 'evaluation' ? 'secondary' : 'ghost'}
                  className="h-7 text-xs"
                  data-testid="activity-scope-evaluation"
                  onClick={() => setActivityScope('evaluation')}
                >
                  This evaluation
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={activityScope === 'all' ? 'secondary' : 'ghost'}
                  className="h-7 text-xs"
                  data-testid="activity-scope-all"
                  onClick={() => setActivityScope('all')}
                >
                  All evaluations
                </Button>
              </div>
            </div>
            {activityLoading ? (
              <p className="mt-6 text-sm text-muted-foreground" data-testid="activity-loading">Loading activity…</p>
            ) : activityError ? (
              <p className="mt-6 text-sm text-destructive" data-testid="activity-error">Could not load activity.</p>
            ) : activityItems.length === 0 ? (
              <div className="mt-6 rounded-lg border bg-secondary/40 p-6 text-sm text-muted-foreground" data-testid="activity-empty-state">
                No activity recorded yet. Once HR generates links, schedules panels, or makes status changes,
                they will appear here.
              </div>
            ) : (
              <ul className="mt-4 space-y-3" data-testid="activity-list">
                {activityItems.map((a) => (
                  <li key={a.id} className="flex items-start gap-3 rounded-lg border-l-2 border-primary/40 bg-secondary/30 p-3" data-testid={`activity-row-${a.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm">
                        <span className="font-semibold">{a.actor}</span>{' '}
                        <span className="text-muted-foreground">{a.description || a.action}</span>
                        {a.reason && <span className="text-muted-foreground"> — {a.reason}</span>}
                      </p>
                      <p className="mt-0.5 text-xs text-muted-foreground tabular-nums">
                        {a.timestamp ? fmtDateTimeIST(a.timestamp) : ''}
                        {activityScope === 'all' && a.evaluation_id ? ' · evaluation-scoped' : ''}
                        {activityScope === 'all' && !a.evaluation_id ? ' · candidate-level' : ''}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      {/* Generate link dialog */}
      <Dialog open={genLinkOpen} onOpenChange={setGenLinkOpen}>
        <DialogContent data-testid="generate-link-dialog">
          <DialogHeader>
            <DialogTitle>Generate {STAGE_LABEL[genLinkStage]} link</DialogTitle>
            <DialogDescription>The candidate will receive the assessment link by email.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Stage</label>
              <Select value={genLinkStage} onValueChange={setGenLinkStage}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="VIDEOBOT">Video Bot</SelectItem>
                  <SelectItem value="MCQ">IQ Assessment</SelectItem>
                  {pipelineStageSet.has('tech_assessment') && (
                    <SelectItem value="TECH_ASSESSMENT">Tech Assessment</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Link expiry</label>
              <Select value={String(genLinkExpiry)} onValueChange={(v) => setGenLinkExpiry(Number(v))}>
                <SelectTrigger data-testid="link-expiry-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="24">24 hours</SelectItem>
                  <SelectItem value="48">48 hours</SelectItem>
                  <SelectItem value="72">72 hours</SelectItem>
                  <SelectItem value="168">7 days</SelectItem>
                  <SelectItem value="336">14 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {assessmentReadiness.getStageWarning(genLinkStage) && (
              <div
                className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50/40 p-3 text-xs text-amber-900"
                data-testid="generate-link-config-warning"
              >
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{assessmentReadiness.getStageWarning(genLinkStage)}</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setGenLinkOpen(false)}>Cancel</Button>
            <Button
              onClick={generateLink}
              disabled={!assessmentReadiness.isStageReady(genLinkStage)}
              data-testid="generate-link-confirm-button"
            >
              Generate &amp; send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Profile dialog was removed in Sprint 11.5 — profile editing now
          lives in the dedicated Resume Enrichment workspace
          (/portal/resume-enrichment/:candidateId). The header "Edit in
          Enrichment workspace" button is the entry point. */}

      {/* Sprint 14.1b — New Evaluation modal + Close Evaluation confirmation.
          Replaces the legacy disabled "New interview record" scaffold. */}
      {canCreateEvaluation && (
        <NewEvaluationModal
          open={newEvalOpen}
          onOpenChange={setNewEvalOpen}
          candidateId={id}
          initialName={`Evaluation ${evaluations.length + 1}`}
          initialRoleId={c.role_id || ''}
          initialDepartmentId={c.department_id || ''}
          initialSubDepartmentId={c.sub_department_id || ''}
          initialPipelineId={c.pipeline_id || ''}
          onCreated={async (created) => {
            await refetchEvaluations();
            setSelectedEvaluationId(created.id);
            setActiveRecordId(created.id);
          }}
        />
      )}

      {canCloseEvaluation && (
        <CloseEvaluationDialog
          open={closeEvalOpen}
          onOpenChange={setCloseEvalOpen}
          evaluation={evaluations.find(
            (e) => e.id === effectiveEvaluationId,
          )}
          onClosed={async () => {
            await refetchEvaluations();
          }}
        />
      )}

      {/* Sprint 11.5 — Legacy "Share with client" dialog removed from header.
          Client sharing is now exclusively driven from the Client Links tab,
          which enforces the artefact checklist + HR cover note workflow. */}

      <CompanyResumeViewModal
        resumeViewOpen={resumeViewOpen}
        setResumeViewOpen={setResumeViewOpen}
        candidateName={c.full_name}
        companyResume={companyResume}
        resumeTemplate={resumeTemplate}
        photoUrl={c.photo_url}
        generating={generating}
        generateResume={generateResume}
        canReplaceResume={canReplaceResume}
      />
    </div>
  );
}
